import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbDatepickerModule, NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { collapseExpand } from '../../animations/animations';
import { CommonModule } from '@angular/common';
import { DateConversionService } from './services/date-conversion.service';
import { DateSelectionService } from './services/date-selection.service';
import { CalendarFooterComponent } from './components/calendar-footer/calendar-footer.component';
import { CalendarDateTypeComponent } from './components/calendar-date-type/calendar-date-type.component';
import { CalendarDateSelectionComponent } from './components/calendar-date-selection/calendar-date-selection.component';
import { CalendarInputComponent } from './components/calendar-input/calendar-input.component';
import { CalendarNavigationComponent } from './components/calendar-navigation/calendar-navigation.component';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { ClickOutsideModule } from '../../directives';
import {
  DatePickerAdvancedChanges,
  DatePickerAdvancedLocale,
  DatePickerAdvancedRange,
  DatePickerDateType,
  DatepickerInterval,
  DefaultLocale,
  DefaultRange,
} from './datepicker-range-advanced.interfaces';
import { CalendarQuickSelectionComponent } from './components/calendar-quick-selection/calendar-quick-selection.component';
import { subDays } from 'date-fns';
import { LayoutService, SelectOption } from '@rx-applications/shared/utils';
import { PillComponent } from '../pill/pill.component';
import { RxButtonModule } from '../button/button.module';
import { RxTabModule } from '../tab/tab.module';
import { TabGroupComponent } from '../tab-group/tab-group.component';
import { TabItemComponent } from '../tab-group/tab-item/tab-item.component';
import { CalendarMonthsComponent } from './components/calendar-months/calendar-months.component';
import { DatepickerTab } from '../../constants';
import { Subject } from 'rxjs';

@Component({
  standalone: true,
  selector: 'rx-datepicker-range-advanced',
  templateUrl: './datepicker-range-advanced.component.html',
  styleUrls: ['./datepicker-range-advanced.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgbDatepickerModule,
    CalendarFooterComponent,
    CalendarDateTypeComponent,
    CalendarDateSelectionComponent,
    CalendarInputComponent,
    CalendarNavigationComponent,
    NgbDropdownModule,
    ClickOutsideModule,
    PillComponent,
    CalendarQuickSelectionComponent,
    RxButtonModule,
    RxTabModule,
    TabGroupComponent,
    TabItemComponent,
    CalendarMonthsComponent,
  ],
  providers: [DateSelectionService],
  animations: [collapseExpand],
})
export class RxDatePickerRangeAdvancedComponent implements OnInit {
  layoutService = inject(LayoutService);
  dateSelectionService = inject(DateSelectionService);
  dateConversionService = inject(DateConversionService);
  cdr = inject(ChangeDetectorRef);
  monthList: SelectOption[] = [];
  yearList: SelectOption[] = [];
  datePickerRange: DatePickerAdvancedRange = DefaultRange;
  datePickerLocale: DatePickerAdvancedLocale = DefaultLocale;

  DatepickerTab = DatepickerTab;
  activeTab: DatepickerTab;

  resetSelectedTab$ = new Subject<boolean>();
  resetTabSelections$ = new Subject<boolean>();

  @Input() set locale(locale: Partial<DatePickerAdvancedLocale>) {
    this.datePickerLocale = { ...DefaultLocale, ...locale };
  }

  @Input() set dateRange({ startDate, endDate, minDate, maxDate, dateType }: Partial<DatePickerAdvancedRange>) {
    const validatedStartDate: string | Date = new Date(this.dateSelectionService.getStartDate(startDate, minDate, maxDate));
    const validatedEndDate: string | Date = new Date(this.dateSelectionService.getEndDate(endDate, minDate, maxDate));

    this.datePickerRange = {
      ...DefaultRange,
      dateType,
      startDate: validatedStartDate,
      endDate: validatedEndDate,
      minDate: minDate ? new Date(minDate) : DefaultRange.minDate,
      maxDate: maxDate ? new Date(maxDate) : DefaultRange.maxDate,
    };

    this.dateSelectionService.setDates({
      startDate: validatedStartDate,
      endDate: validatedEndDate,
    });
    this.cdr.detectChanges();
  }

  // Is dropdown open state
  @Input() isOpen = false;

  @Input() showTabs = false;

  // Allows to select the same checkin and checkout date
  @Input() allowSameDaySelection = false;

  // Date type radio options
  @Input() dateTypeOptions?: DatePickerDateType[];

  // Test id prefix for testing
  @Input() testIdPrefix = 'test-date-picker';

  // Placement of dropdown
  @Input() placement: PlacementArray = ['bottom-left', 'bottom-right', 'top-left', 'top-right'];

  // Compact mode
  @Input() compact = false;

  // Date range selected
  @Output() dateSelected = new EventEmitter<DatePickerAdvancedChanges>();
  @Output() dateTypeSelected = new EventEmitter<string>();

  @ViewChild('dateSelection') dateSelection: CalendarDateSelectionComponent;
  @ViewChild('dropdown') dropdown: NgbDropdown;

  selectedDateType: string | null = null;
  dateFormat: string;

  ngOnInit(): void {
    this.dateFormat = this.dateConversionService.getDateFormat();
    this.dateSelectionService.setDates({
      startDate: <Date>this.datePickerRange.startDate,
      endDate: <Date>this.datePickerRange.endDate,
    });
  }

  handleClickOutside(): void {
    if (this.dropdown?.isOpen()) {
      this.onResetDateRange();
    }
  }

  onApplyDateRange(): void {
    const datesSelected: DatePickerAdvancedChanges = this.dateSelectionService.getDatesChangeEvent(this.selectedDateType);
    this.dateSelected.emit(datesSelected);
    this.datePickerRange = {
      ...this.datePickerRange,
      startDate: datesSelected.startDate,
      endDate: datesSelected.endDate,
    };
    this.dropdown.close();
  }

  onResetDateRange(): void {
    this.dateSelectionService.setDates({
      startDate: <Date>this.datePickerRange.startDate,
      endDate: <Date>this.datePickerRange.endDate,
    });
    this.dropdown.close();
  }

  onDateTypeChange(dateType: string): void {
    this.selectedDateType = dateType ?? null;
    this.datePickerRange = { ...this.datePickerRange, dateType: this.selectedDateType };
    this.dateTypeSelected.emit(this.selectedDateType);
    this.resetNavigation();
    this.resetSelectedTab();
  }

  onQuickSelection(days: number): void {
    this.dateSelectionService.setDates({
      startDate: subDays(new Date(), days),
      endDate: subDays(new Date(), 0),
    });
  }

  private resetNavigation(): void {
    this.dateSelection.datePicker.onDateSelect(this.dateConversionService.toNgbDate(this.datePickerRange.startDate));
    this.dateSelectionService.setDates({
      startDate: <Date>this.datePickerRange.startDate,
      endDate: <Date>this.datePickerRange.endDate,
    });
  }

  selectionChange(selectedDates: DatepickerInterval): void {
    this.dateSelectionService.setDates({
      startDate: selectedDates.startDate,
      endDate: selectedDates.endDate,
    });
  }

  onTabChange(selectedTab: DatepickerTab): void {
    this.activeTab = selectedTab;
    if (selectedTab !== DatepickerTab.CustomRange) {
      this.resetTabSelections();
      this.dateSelectionService.setDates({
        startDate: null,
        endDate: null,
      });
    }
  }

  onDropdownChange(dropdownOpen: boolean): void {
    if (!dropdownOpen) {
      this.resetSelectedTab();
    }
  }

  private resetSelectedTab(): void {
    this.resetSelectedTab$.next(true);
  }

  private resetTabSelections(): void {
    this.resetTabSelections$.next(true);
  }
}
